<template>
  <transition name="collapsable"
              v-on:before-enter="collapseBody"
              v-on:enter="expandBody"
              v-on:before-leave="expandBody"
              v-on:leave="collapseBody"
  >
    <div v-show="open"
         ref="bodyRef"
         class="collapsable"
         :class="{expanded: open}"
    >
      <div ref="contentRef"
           class="collapsable__content"
           :class="contentClass"
      >
        <slot />
      </div>
    </div>
  </transition>
</template>
<script setup>
import {nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch} from "vue";

const props = defineProps({
  expanded: Boolean,
  disabled: Boolean,
  contentClass: [String, Array, Object],
});
const emit = defineEmits(['update:expanded', 'change']);
const open = ref(!!props.expanded);
const bodyRef = ref();
const contentRef = ref();
const contentObserver = ref();
let interactionObserver = null;
let resizeObserver = null;
const slots = useSlots();

watch(
    () => props.expanded,
    (value) => {
      if (props.disabled) return;
      value ? expand() : collapse();
    }
);

watch(
    () => open.value,
    (value) => {
      if (props.disabled) return;
      emit('change', value);
      emit('update:expanded', value);
    }
);

onMounted(() => {
  nextTick(observeBody);
});

function observeBody() {
  if (!contentRef.value) return;
  contentObserver.value = new MutationObserver((mutationList, observer) => {
    nextTick(expandBody);
  });

  interactionObserver = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    nextTick(expandBody);
  });

  resizeObserver = new ResizeObserver(() => {
    if (!open.value) return;
    nextTick(expandBody);
  });

  if (contentRef.value) {
    contentObserver.value.observe(contentRef.value, { childList: true, subtree: true, attributes: true });
    resizeObserver.observe(contentRef.value);
  }

  if (bodyRef.value) {
    interactionObserver.observe(bodyRef.value);
  }
}

onBeforeUnmount(() => {
  contentObserver.value?.disconnect();
  interactionObserver?.disconnect();
  resizeObserver?.disconnect();
});

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
}

function expand() {
  open.value = true;
}

function collapse() {
  open.value = false;
}

function collapseBody() {
  if (!bodyRef.value) return;
  bodyRef.value.style.height = 0;
}


function expandBody() {
  if (!bodyRef.value) return;
  bodyRef.value.style.height = `${contentRef.value.scrollHeight}px`;
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.collapsable {
  overflow: hidden;
  transition: height $transition-timing;
  will-change: height;
}
</style>